/**
 * Разделы предпросмотра импорта книги ЭММ: счётчики, изменённые и новые
 * модели с отметками, новые категории, предупреждения разбора, «нет в книге».
 */
import { useId, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import type { FleetImportModel, FleetImportResult, FleetImportSummary } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { IconChevronDown } from '@shared/ui/icons';
import {
  DiffList,
  FleetChip,
  PrcMark,
  fleetLeading,
  fleetType,
  formatCount,
  typo,
} from '@entities/fleet';

export type ChangedModel = Extract<FleetImportModel, { status: 'changed' }>;
export type NewModel = Extract<FleetImportModel, { status: 'new' }>;

const Tiles = styled.dl<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: 1px;
  margin: 0 0 ${({ theme }) => theme.spacing(6)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;

  & > div {
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  & dt {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    font-variant-numeric: tabular-nums;
  }

  & dd > .note {
    display: block;
    margin-top: ${({ theme }) => theme.spacing(1)};
    font-size: ${fleetType.small};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  @container drawer (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export function Counters({ summary }: { summary: FleetImportSummary }) {
  const { counts } = summary;
  const tiles: { label: string; value: number; note?: string }[] = [
    { label: 'Новые модели', value: counts.new },
    {
      label: 'Изменённые',
      value: counts.changed,
      note:
        counts.prc_changes > 0 ? `с полями ПРЦ — ${formatCount(counts.prc_changes)}` : undefined,
    },
    { label: 'Без изменений', value: counts.unchanged },
    { label: 'Нет в книге', value: counts.absent },
    { label: 'Новые категории', value: summary.categories_new.length },
    { label: 'Предупреждения', value: counts.issues },
  ];
  return (
    <Tiles $cols={3} aria-label="Что изменится">
      {tiles.map((t) => (
        <div key={t.label}>
          <dt>{t.label}</dt>
          <dd>
            {formatCount(t.value)}
            {t.note && <span className="note">{t.note}</span>}
          </dd>
        </div>
      ))}
    </Tiles>
  );
}

/** Итог применения — теми же плитками, что и предпросмотр. */
export function ResultTiles({ result }: { result: FleetImportResult }) {
  const tiles: { label: string; value: number }[] = [
    { label: 'Создано моделей', value: result.created },
    { label: 'Обновлено', value: result.updated },
    { label: 'Без изменений', value: result.unchanged },
    { label: 'Новых категорий', value: result.categories_created },
  ];
  return (
    <Tiles $cols={4} aria-label="Итог загрузки">
      {tiles.map((t) => (
        <div key={t.label}>
          <dt>{t.label}</dt>
          <dd>{formatCount(t.value)}</dd>
        </div>
      ))}
    </Tiles>
  );
}

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};

  & > h3 {
    margin: 0;
    font-size: ${fleetType.lead};
    font-weight: 600;
  }

  & > .count {
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  & > .tools {
    margin-left: auto;
  }
`;

const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  & > input {
    flex: none;
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
`;

const Rows = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const RowItem = styled.li`
  padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const RowLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};

  & > label {
    flex: 1 1 260px;
    min-width: 0;
    align-items: flex-start;
  }

  & > label > input {
    margin-top: 2px;
  }

  & .name {
    display: block;
    font-size: ${fleetType.base};
    font-weight: 500;
    line-height: ${fleetLeading.tight};
    overflow-wrap: anywhere;
  }

  & .sub {
    display: block;
    margin-top: 2px;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > .meta {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }
`;

const Expand = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} 0`};
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 600;
  cursor: pointer;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }
`;

const DiffBox = styled.div`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 ${theme.spacing(1)} ${theme.spacing(6)}`};

  @container drawer (max-width: 520px) {
    margin-left: 0;
  }
`;

const Chips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Issues = styled.ul`
  margin: 0;
  padding: 0 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > li + li {
    margin-top: ${({ theme }) => theme.spacing(1.5)};
  }

  & > li::marker {
    color: ${({ theme }) => theme.colors.status.warning};
  }
`;

const Names = styled.ul`
  columns: 2 260px;
  column-gap: ${({ theme }) => theme.spacing(6)};
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > li {
    break-inside: avoid;
    padding: ${({ theme }) => `${theme.spacing(0.5)} 0`};
    overflow-wrap: anywhere;
  }
`;

const More = styled.button`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: ${fleetType.body};
  font-weight: 600;
  cursor: pointer;
`;

/** Флажок «отметить всё в разделе» с промежуточным состоянием. */
function SectionToggle({
  label,
  checked,
  total,
  onChange,
}: {
  label: string;
  checked: number;
  total: number;
  onChange: (value: boolean) => void;
}) {
  return (
    <Check className="tools">
      <input
        type="checkbox"
        checked={checked === total && total > 0}
        ref={(el) => {
          if (el) el.indeterminate = checked > 0 && checked < total;
        }}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </Check>
  );
}

function ChangedRow({
  model,
  checked,
  onToggle,
}: {
  model: ChangedModel;
  checked: boolean;
  onToggle: (value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const diffId = useId();
  const fields = Object.keys(model.diff).length;
  return (
    <RowItem>
      <RowLine>
        <Check>
          <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
          <span>
            <span className="name">{model.name}</span>
            {model.category && <span className="sub">{model.category}</span>}
          </span>
        </Check>
        <span className="meta">
          {formatCount(fields)} {pluralizeRu(fields, ['поле', 'поля', 'полей'])}
          {model.prc_fields_changed && (
            <PrcMark title="Изменились поля, от которых зависит цена машино-часа: после применения модель получит пометку «ПРЦ устарела»">
              ПРЦ
            </PrcMark>
          )}
          <Expand
            type="button"
            aria-expanded={open}
            aria-controls={diffId}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Скрыть изменения' : 'Было → стало'}
            <IconChevronDown size={14} />
          </Expand>
        </span>
      </RowLine>
      <DiffBox id={diffId} hidden={!open}>
        {open && <DiffList diff={model.diff} label={`Изменения: ${model.name}`} />}
      </DiffBox>
    </RowItem>
  );
}

export function ChangedSection({
  models,
  selected,
  onToggle,
  onToggleAll,
}: {
  models: readonly ChangedModel[];
  selected: ReadonlySet<string>;
  onToggle: (name: string, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
}) {
  const checked = models.filter((m) => selected.has(m.name)).length;
  return (
    <Section aria-labelledby="fleet-import-changed">
      <SectionHead>
        <h3 id="fleet-import-changed">Изменённые модели</h3>
        <span className="count">{formatCount(models.length)}</span>
        <SectionToggle
          label="Все изменённые"
          checked={checked}
          total={models.length}
          onChange={onToggleAll}
        />
      </SectionHead>
      <Lead>
        {typo(
          'Обновятся только поля из книги. Правки, внесённые в справочник после загрузки предпросмотра, сравниваются заново при применении.',
        )}
      </Lead>
      <Rows>
        {models.map((m) => (
          <ChangedRow
            key={m.name}
            model={m}
            checked={selected.has(m.name)}
            onToggle={(v) => onToggle(m.name, v)}
          />
        ))}
      </Rows>
    </Section>
  );
}

export function NewSection({
  models,
  selected,
  onToggle,
  onToggleAll,
}: {
  models: readonly NewModel[];
  selected: ReadonlySet<string>;
  onToggle: (name: string, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
}) {
  const checked = models.filter((m) => selected.has(m.name)).length;
  return (
    <Section aria-labelledby="fleet-import-new">
      <SectionHead>
        <h3 id="fleet-import-new">Новые модели</h3>
        <span className="count">{formatCount(models.length)}</span>
        <SectionToggle
          label="Все новые"
          checked={checked}
          total={models.length}
          onChange={onToggleAll}
        />
      </SectionHead>
      <Rows>
        {models.map((m) => (
          <RowItem key={m.name}>
            <RowLine>
              <Check>
                <input
                  type="checkbox"
                  checked={selected.has(m.name)}
                  onChange={(e) => onToggle(m.name, e.target.checked)}
                />
                <span>
                  <span className="name">{m.name}</span>
                  {m.category && <span className="sub">{m.category}</span>}
                </span>
              </Check>
              {m.units_count !== undefined && (
                <span className="meta">{formatCount(m.units_count)} ед.</span>
              )}
            </RowLine>
          </RowItem>
        ))}
      </Rows>
    </Section>
  );
}

function CollapsibleNames({ names, limit = 12 }: { names: readonly string[]; limit?: number }) {
  const [all, setAll] = useState(false);
  const listId = useId();
  const shown = all ? names : names.slice(0, limit);
  return (
    <>
      <Names id={listId}>
        {shown.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </Names>
      {names.length > limit && (
        <More
          type="button"
          aria-expanded={all}
          aria-controls={listId}
          onClick={() => setAll((v) => !v)}
        >
          {all ? 'Свернуть' : `Показать все ${formatCount(names.length)}`}
        </More>
      )}
    </>
  );
}

export function InfoSections({
  summary,
  unchanged,
}: {
  summary: FleetImportSummary;
  unchanged: readonly string[];
}): ReactNode {
  return (
    <>
      {summary.categories_new.length > 0 && (
        <Section aria-labelledby="fleet-import-categories">
          <SectionHead>
            <h3 id="fleet-import-categories">Новые категории</h3>
            <span className="count">{formatCount(summary.categories_new.length)}</span>
          </SectionHead>
          <Lead>{typo('Появятся в справочнике вместе с моделями, которые к ним относятся.')}</Lead>
          <Chips>
            {summary.categories_new.map((c) => (
              <li key={c}>
                <FleetChip $tone="info">{c}</FleetChip>
              </li>
            ))}
          </Chips>
        </Section>
      )}
      {summary.issues.length > 0 && (
        <Section aria-labelledby="fleet-import-issues">
          <SectionHead>
            <h3 id="fleet-import-issues">Предупреждения разбора</h3>
            <span className="count">{formatCount(summary.issues.length)}</span>
          </SectionHead>
          <Lead>
            {typo(
              'Применению не мешают, но стоит проверить книгу: эти значения не попадут в расчёт как есть.',
            )}
          </Lead>
          <Issues>
            {summary.issues.map((i, n) => (
              <li key={`${i.kind}-${i.row ?? ''}-${n}`}>{i.message}</li>
            ))}
          </Issues>
        </Section>
      )}
      {summary.absent.length > 0 && (
        <Section aria-labelledby="fleet-import-absent">
          <SectionHead>
            <h3 id="fleet-import-absent">Нет в книге</h3>
            <span className="count">{formatCount(summary.absent.length)}</span>
          </SectionHead>
          <Lead>
            {typo(
              'Эти модели есть в справочнике, но в книге их нет. Автоматически они не архивируются — если техника выбыла, отправьте её в архив из карточки.',
            )}
          </Lead>
          <CollapsibleNames names={summary.absent} />
        </Section>
      )}
      {unchanged.length > 0 && (
        <Section aria-labelledby="fleet-import-unchanged">
          <SectionHead>
            <h3 id="fleet-import-unchanged">Без изменений</h3>
            <span className="count">{formatCount(unchanged.length)}</span>
          </SectionHead>
          <CollapsibleNames names={unchanged} limit={6} />
        </Section>
      )}
    </>
  );
}
